import { Extension } from '@tiptap/core';

// Block-level indent for paragraphs and headings (list items keep using
// sinkListItem/liftListItem). Stored as an `indent` attribute rendered as a
// margin-left, so it round-trips through the saved HTML.

const MAX_INDENT = 8;
const INDENT_PX = 40;

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        indent: {
            indent: () => ReturnType;
            outdent: () => ReturnType;
        };
    }
}

export const Indent = Extension.create({
    name: 'indent',

    addGlobalAttributes() {
        return [
            {
                types: ['paragraph', 'heading'],
                attributes: {
                    indent: {
                        default: 0,
                        parseHTML: (element) => {
                            const margin = parseInt(element.style.marginLeft || '0', 10);
                            if (Number.isNaN(margin) || margin <= 0) return 0;
                            return Math.min(MAX_INDENT, Math.round(margin / INDENT_PX));
                        },
                        renderHTML: (attributes) => {
                            if (!attributes.indent) return {};
                            return { style: `margin-left: ${attributes.indent * INDENT_PX}px` };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        const changeIndent = (delta: number) =>
            () =>
            ({ tr, state, dispatch }: any) => {
                const { from, to } = state.selection;
                let changed = false;
                state.doc.nodesBetween(from, to, (node: any, pos: number) => {
                    if (node.type.name !== 'paragraph' && node.type.name !== 'heading') {
                        return;
                    }
                    const current = node.attrs.indent || 0;
                    const next = Math.min(MAX_INDENT, Math.max(0, current + delta));
                    if (next !== current) {
                        tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
                        changed = true;
                    }
                });
                if (changed && dispatch) {
                    dispatch(tr);
                }
                return changed;
            };

        return {
            indent: changeIndent(1),
            outdent: changeIndent(-1),
        };
    },
});
